/**
 * The structure of the PRISMA 2020 checklists: which items exist, and under which section
 * and topic they sit. The wording of each item is the widget's string, so a translation
 * can supply it.
 *
 * The 27-item checklist (42 rows once the lettered sub-items are counted) and the
 * 12-item abstract checklist are from Page MJ, McKenzie JE, Bossuyt PM, et al. The PRISMA
 * 2020 statement: an updated guideline for reporting systematic reviews. BMJ
 * 2021;372:n71, Tables 1 and 2. doi:10.1136/bmj.n71. The article is published under the
 * Creative Commons Attribution 4.0 licence (CC BY 4.0).
 */

export interface PrismaItem {
  /** "1", "10a"… */
  id: string;
  section: string;
  topic: string;
}

export type ChecklistKind = "full" | "abstract";

export const PRISMA_ITEMS: readonly PrismaItem[] = [
  { id: "1", section: "title", topic: "title" },
  { id: "2", section: "abstract", topic: "abstract" },
  { id: "3", section: "introduction", topic: "rationale" },
  { id: "4", section: "introduction", topic: "objectives" },
  { id: "5", section: "methods", topic: "eligibilityCriteria" },
  { id: "6", section: "methods", topic: "informationSources" },
  { id: "7", section: "methods", topic: "searchStrategy" },
  { id: "8", section: "methods", topic: "selectionProcess" },
  { id: "9", section: "methods", topic: "dataCollectionProcess" },
  { id: "10a", section: "methods", topic: "dataItems" },
  { id: "10b", section: "methods", topic: "dataItems" },
  { id: "11", section: "methods", topic: "studyRiskOfBiasAssessment" },
  { id: "12", section: "methods", topic: "effectMeasures" },
  { id: "13a", section: "methods", topic: "synthesisMethods" },
  { id: "13b", section: "methods", topic: "synthesisMethods" },
  { id: "13c", section: "methods", topic: "synthesisMethods" },
  { id: "13d", section: "methods", topic: "synthesisMethods" },
  { id: "13e", section: "methods", topic: "synthesisMethods" },
  { id: "13f", section: "methods", topic: "synthesisMethods" },
  { id: "14", section: "methods", topic: "reportingBiasAssessment" },
  { id: "15", section: "methods", topic: "certaintyAssessment" },
  { id: "16a", section: "results", topic: "studySelection" },
  { id: "16b", section: "results", topic: "studySelection" },
  { id: "17", section: "results", topic: "studyCharacteristics" },
  { id: "18", section: "results", topic: "riskOfBiasInStudies" },
  { id: "19", section: "results", topic: "resultsOfIndividualStudies" },
  { id: "20a", section: "results", topic: "resultsOfSyntheses" },
  { id: "20b", section: "results", topic: "resultsOfSyntheses" },
  { id: "20c", section: "results", topic: "resultsOfSyntheses" },
  { id: "20d", section: "results", topic: "resultsOfSyntheses" },
  { id: "21", section: "results", topic: "reportingBiases" },
  { id: "22", section: "results", topic: "certaintyOfEvidence" },
  { id: "23a", section: "discussion", topic: "discussion" },
  { id: "23b", section: "discussion", topic: "discussion" },
  { id: "23c", section: "discussion", topic: "discussion" },
  { id: "23d", section: "discussion", topic: "discussion" },
  { id: "24a", section: "other", topic: "registrationAndProtocol" },
  { id: "24b", section: "other", topic: "registrationAndProtocol" },
  { id: "24c", section: "other", topic: "registrationAndProtocol" },
  { id: "25", section: "other", topic: "support" },
  { id: "26", section: "other", topic: "competingInterests" },
  {
    id: "27",
    section: "other",
    topic: "availabilityOfDataCodeAndOtherMaterials",
  },
];

export const PRISMA_ABSTRACT_ITEMS: readonly PrismaItem[] = [
  { id: "1", section: "title", topic: "title" },
  { id: "2", section: "background", topic: "objectives" },
  { id: "3", section: "methods", topic: "eligibilityCriteria" },
  { id: "4", section: "methods", topic: "informationSources" },
  { id: "5", section: "methods", topic: "riskOfBias" },
  { id: "6", section: "methods", topic: "synthesisOfResults" },
  { id: "7", section: "results", topic: "includedStudies" },
  { id: "8", section: "results", topic: "synthesisOfResults" },
  { id: "9", section: "discussion", topic: "limitationsOfEvidence" },
  { id: "10", section: "discussion", topic: "interpretation" },
  { id: "11", section: "other", topic: "funding" },
  { id: "12", section: "other", topic: "registration" },
];

export interface ItemEntry {
  location: string;
  status: "" | "reported" | "partly" | "na";
}

export type Entries = Readonly<Record<string, ItemEntry>>;

export const EMPTY_ENTRY: ItemEntry = { location: "", status: "" };

export function itemsFor(kind: ChecklistKind): readonly PrismaItem[] {
  return kind === "full" ? PRISMA_ITEMS : PRISMA_ABSTRACT_ITEMS;
}

/** Entries are keyed by checklist and item, so switching checklists keeps both. */
export function entryKey(kind: ChecklistKind, id: string): string {
  return `${kind}:${id}`;
}

export function entryFor(
  entries: Entries,
  kind: ChecklistKind,
  id: string
): ItemEntry {
  return entries[entryKey(kind, id)] ?? EMPTY_ENTRY;
}

/** Items with a location written in or a status chosen. */
export function filledCount(entries: Entries, kind: ChecklistKind): number {
  return itemsFor(kind).filter((item) => {
    const entry = entryFor(entries, kind, item.id);
    return entry.location.trim() !== "" || entry.status !== "";
  }).length;
}

/** Whether `item` opens a new section or a new topic compared with the one before it. */
export function rowStarts(
  items: readonly PrismaItem[],
  index: number
): { section: boolean; topic: boolean } {
  const previous = items[index - 1];
  const current = items[index];
  return {
    section: !previous || previous.section !== current.section,
    topic: !previous || previous.topic !== current.topic,
  };
}
